"use client";

import { getBaseUrl } from "@repo/common/base-url";
import type { ClientReferralLinkSource } from "@repo/db";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { api } from "@/lib/trpc/react";

interface UseOpportunityShareLinkOptions {
  token: string;
  source: ClientReferralLinkSource;
  enabled?: boolean;
  // Defers minting the link until `prefetchLink()` or `copyLink()` is invoked.
  // Use on browsing grids: hovering N cards would otherwise mint N rows and
  // fire N `referral_link_minted` events, inflating the per-surface mint
  // denominator without proportional downstream events.
  lazy?: boolean;
}

export function useOpportunityShareLink({
  token,
  source,
  enabled = true,
  lazy = false,
}: UseOpportunityShareLinkOptions) {
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const inFlightRef = useRef(false);
  const posthog = usePostHog();
  const utils = api.useUtils();

  const {
    data: referralLinkData,
    isLoading,
    isError,
  } = api.referral.getOrCreateOpportunityLink.useQuery(
    { opportunityToken: token, source },
    { enabled: enabled && !lazy, retry: false },
  );

  const baseUrl = getBaseUrl();
  const plainUrl = `${baseUrl}/opportunities/${token}`;
  const shareUrl = referralLinkData?.url ?? plainUrl;
  const hasReferralLink = Boolean(referralLinkData?.url);
  const displayLink = shareUrl.replace(/^https?:\/\//, "");
  const linkId = referralLinkData?.id ?? null;
  const opportunityListingId = referralLinkData?.opportunityListingId ?? null;

  useEffect(() => {
    return () => {
      clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const flashCopied = useCallback(() => {
    setCopied(true);
    clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => {
      setCopied(false);
    }, 2000);
  }, []);

  // Eager-mode capture: data is already in cache from the mount-time
  // `useQuery`. Lazy callers go through `copyLink`'s fetch path instead.
  const captureCopied = useCallback(
    (linkIdValue: string | null, listingIdValue: string | null) => {
      // Unified event shape across all referral-share surfaces:
      // { source, linkId, opportunityListingId }. Keeps PostHog breakdowns
      // joinable per surface without per-call conditionals.
      posthog.capture("referral_link_copied", {
        source,
        linkId: linkIdValue,
        opportunityListingId: listingIdValue,
      });
    },
    [posthog, source],
  );

  const prefetchLink = useCallback(() => {
    if (!enabled || !token) {
      return;
    }
    void utils.referral.getOrCreateOpportunityLink.prefetch({
      opportunityToken: token,
      source,
    });
  }, [enabled, token, source, utils]);

  const copyLink = useCallback(async () => {
    // Eager mode: the link is already in cache from the mount-time query.
    if (!lazy) {
      try {
        await navigator.clipboard?.writeText(shareUrl);
        captureCopied(linkId, opportunityListingId);
        flashCopied();
      } catch {
        // Swallow: clipboard errors surface as the pill not flashing "Copied".
      }
      return;
    }

    if (!enabled || !token) {
      return;
    }

    // Fast path: already minted -> copy in-gesture, flash now (no delay).
    const cached = utils.referral.getOrCreateOpportunityLink.getData({
      opportunityToken: token,
      source,
    });
    if (cached?.url) {
      void navigator.clipboard?.writeText(cached.url)?.catch(() => {});
      captureCopied(cached.id, cached.opportunityListingId);
      flashCopied();
      return;
    }

    // Not minted yet: flash only after the write lands. An optimistic flash
    // could show "Copied" on touch, where the post-await write loses the
    // user-activation gesture and silently fails.
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    try {
      const link = await utils.referral.getOrCreateOpportunityLink.fetch({
        opportunityToken: token,
        source,
      });
      await navigator.clipboard?.writeText(link.url);
      captureCopied(link.id, link.opportunityListingId);
      flashCopied();
    } catch {
      // Nothing copied, so no flash.
    } finally {
      inFlightRef.current = false;
    }
  }, [
    lazy,
    enabled,
    token,
    source,
    utils,
    shareUrl,
    linkId,
    opportunityListingId,
    captureCopied,
    flashCopied,
  ]);

  return {
    shareUrl,
    displayLink,
    copied,
    copyLink,
    prefetchLink,
    isLoading,
    isError,
    hasReferralLink,
  };
}
